/**
 * Reads the text of a manual or policy in the browser, so the company sees and corrects
 * exactly what the IA will read before saving. pdf.js loads only when a PDF is chosen.
 */

export const KNOWLEDGE_FILE_ACCEPT = ".pdf,.txt,.md,application/pdf,text/plain,text/markdown";
export const KNOWLEDGE_FILE_MAX = 15 * 1024 * 1024;
/** A PDF may be bigger when chosen: it is optimized first, and then has to fit the limit. */
export const KNOWLEDGE_PDF_PICK_MAX = 60 * 1024 * 1024;

/** The type the API expects; browsers often leave .md files without one. */
export function knowledgeFileType(file: File): string | null {
  const name = file.name.toLowerCase();
  if (file.type === "application/pdf" || name.endsWith(".pdf")) return "application/pdf";
  if (name.endsWith(".md") || file.type === "text/markdown") return "text/markdown";
  if (name.endsWith(".txt") || file.type === "text/plain") return "text/plain";
  return null;
}

export async function extractText(file: File): Promise<string> {
  if (knowledgeFileType(file) !== "application/pdf") return (await file.text()).replace(/\r\n/g, "\n").trim();
  const [pdfjs, worker] = await Promise.all([import("pdfjs-dist"), import("pdfjs-dist/build/pdf.worker.min.mjs?url")]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  const task = pdfjs.getDocument({ data: new Uint8Array(await file.arrayBuffer()) });
  const pdf = await task.promise;
  const pages: string[] = [];
  for (let n = 1; n <= pdf.numPages; n++) {
    const content = await (await pdf.getPage(n)).getTextContent();
    let page = "";
    for (const item of content.items) {
      if (!("str" in item)) continue;
      page += item.str + (item.hasEOL ? "\n" : "");
    }
    pages.push(
      page
        .split("\n")
        .map((l) => l.replace(/\s+/g, " ").trim())
        .join("\n")
        .replace(/\n{3,}/g, "\n\n")
        .trim(),
    );
  }
  await task.destroy();
  return pages.filter(Boolean).join("\n\n");
}
